import { defineComponent, ref, Ref, computed, onMounted, watch, nextTick } from 'vue';
import { HoursEntryModel, HoursOfOperationDisplayType, EntryModel} from '@truckdown/locations';
//import dayEntry from './DayEntry/DayEntry.vue';
import {
    requiresMidnightSplit, DAYS, DayKey,
    deepClone,
    isCarryoverFromPreviousDay
} from '../../../utilities';
import validationError from '../../../Validation/Error/Error.vue';
import { TimeEntryValidator } from '../Validation/TimeEntryValidator';
interface IData {
    allEntries: Ref<{ [key: string]: HoursEntryModel }>,
    localEntries: Ref<{ [key: string]: HoursEntryModel }>,
    entryToAdd: Ref<{ [key: string]: EntryModel | undefined }>,
    isEditing: Ref<Map<EntryModel, boolean>>,
    exceptionValidator: Ref<any>,
    timeValidators: Ref<{ [key: string]: TimeEntryValidator }>
   
}

const getCleanEntries = function (entries: { [key: string]: HoursEntryModel }) {
    if (!entries || Object.keys(entries).length === 0) {
        return getDefaultEntries(entries);
    }

    return Object.fromEntries(
        weekdayOrder
            .filter(day => day in entries)
            .map(day => {
                const original = entries[day];

                // Remove dummy + empty entries
                const cleanedEntries = original.entries.filter(e => {
                    const start = e.startTime?.trim() ?? "";
                    const end = e.endTime?.trim() ?? "";

                    const isZeroEntry = start === "00:00" && end === "00:00" && e.type === 0;
                    const isEmptyEntry = start === "" && end === "" && e.type === 0;

                    return !(isZeroEntry || isEmptyEntry);
                });

       
                // if originally disabled - remain disabled
                // if originally enabled - only true if cleanedEntries > 0
                const enabled =
                    original.enabled === true && cleanedEntries.length > 0;

                return [
                    day,
                    {
                        ...original,
                        entries: cleanedEntries,
                        enabled,
                    }
                ];
            })
    );
};

const weekdayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
function getDefaultEntries(emptyEntries: { [key: string]: HoursEntryModel }) {
    if (Object.keys(emptyEntries).length === 0) {
        return weekdayOrder.reduce((acc, day) => {
            acc[day] = {
                enabled: false,
                afterHours24: false,
                entries: []
            };
            return acc;
        }, {} as { [key: string]: HoursEntryModel });
    }
    return emptyEntries;
}


const getInitialData = function (entries: { [key: string]: HoursEntryModel }): IData {

    let cleanEntries = getCleanEntries(entries)
    return {
        allEntries: ref<{ [key: string]: HoursEntryModel }>(cleanEntries),
        localEntries: ref<{ [key: string]: HoursEntryModel }>(deepClone(cleanEntries)),
        entryToAdd: ref<{ [key: string]: EntryModel | undefined }>({}),
        isEditing: ref<Map<EntryModel, boolean>>(new Map()),
        exceptionValidator: ref<any>(),
        timeValidators: ref<{ [key: string]: TimeEntryValidator }>({})
    };
}

const rebuildTimeValidators = function (data: IData) {
    data.timeValidators.value = {};
    for (const dayKey in data.allEntries.value) {
        const entries = data.allEntries.value[dayKey].entries;
        const typeCounter: { [type: number]: number } = {};
        entries.forEach((entry) => {
            const type = entry.type;
            if (type === 0) return; 

            if (!typeCounter[type]) typeCounter[type] = 0;
            const localIndex = typeCounter[type];

            const key = `${dayKey}-${type}-${localIndex}`;
            const validator = new TimeEntryValidator(data.allEntries.value, dayKey, localIndex, type);
            validator.validate();

            data.timeValidators.value[key] = validator;
            typeCounter[type]++;
        });
    }
}

function timeToMinutes(t?: string): number {
    if (!t) return NaN;
    const [hStr, mStr] = t.trim().split(":");
    const h = Number(hStr);
    const m = Number(mStr);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
    return h * 60 + m;
}

function endMinutesWithMidnightRule(startTime: string, endTime: string): number {
    const s = timeToMinutes(startTime);
    let e = timeToMinutes(endTime);
    if (!Number.isFinite(s) || !Number.isFinite(e)) return NaN;

    // If an entry starts not at midnight and ends at 00:00, that means "end of day" (24:00)
    if (endTime === "00:00" && startTime !== "00:00") {
        e = 1440;
    }
    return e;
}

export function adjustPreviousAndNextDayOverlap(
    allEntries: { [key: string]: HoursEntryModel },
    currentDayKey: string,
    newEntry: EntryModel
) {
    const weekdayOrder = Object.keys(DAYS) as string[];
    const currentIndex = weekdayOrder.indexOf(currentDayKey);

    const newStart = timeToMinutes(newEntry.startTime);
    const newEndRaw = endMinutesWithMidnightRule(newEntry.startTime, newEntry.endTime);
    if (!Number.isFinite(newStart) || !Number.isFinite(newEndRaw)) return;

    const currentEndsNextDay = newEndRaw <= newStart;

    // PREVIOUS DAY: only adjust if previous entry crosses midnight into current day
    const prevDayKey = weekdayOrder[(currentIndex - 1 + 7) % 7];
    const prevDayEntries = allEntries[prevDayKey]?.entries;

    if (prevDayEntries?.length) {
        const lastEntry = [...prevDayEntries]
            .filter(e => e?.startTime && e?.endTime)
            .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))
            .at(-1);

        if (lastEntry) {
            const lastStart = timeToMinutes(lastEntry.startTime);
            const lastEndRaw = endMinutesWithMidnightRule(lastEntry.startTime, lastEntry.endTime);
            if (!Number.isFinite(lastStart) || !Number.isFinite(lastEndRaw)) return;

            const lastEndsNextDay = lastEndRaw <= lastStart;

            // Only if it actually spills into the next day AND overlaps the new start time window
            if (lastEndsNextDay) {
                // Example: prev 20:00�02:00 (endRaw=120), newStart=01:00 => overlap
                const overlapsIntoCurrentDay = newStart < lastEndRaw;

                if (overlapsIntoCurrentDay) {
                    lastEntry.endTime = newEntry.startTime;
                }
            }
        }
    }

    // NEXT DAY: only adjust if current entry crosses midnight into next day
    const nextDayKey = weekdayOrder[(currentIndex + 1) % 7];
    const nextDayEntries = allEntries[nextDayKey]?.entries;

    if (nextDayEntries?.length && currentEndsNextDay) {
        const firstEntry = [...nextDayEntries]
            .filter(e => e?.startTime && e?.endTime)
            .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))[0];

        if (firstEntry) {
            const firstStart = timeToMinutes(firstEntry.startTime);
            if (!Number.isFinite(firstStart)) return;

            // newEndRaw is the "next-day" end in minutes (e.g., 02:00 => 120)
            if (firstStart < newEndRaw) {
                firstEntry.startTime = newEntry.endTime;
            }
        }
    }
}


const getMethods = function (data: IData, ctx: any) {
    const updateEntryState = function (key: string) {
        const entries = data.allEntries.value[key]?.entries ?? [];
        data.allEntries.value[key].enabled = entries.length > 0;
    };

    const removePlaceholderEntries = function (key: string) {
        const entries = data.allEntries.value[key].entries;
        if (entries.length > 0) {
            data.allEntries.value[key].entries = entries.filter(
                (e) =>
                    !(
                        e.type === HoursOfOperationDisplayType.Unknown &&
                        e.startTime === "00:00" &&
                        e.endTime === "00:00"
                    )
            );
        }
    }

    const markEntryEdited = function (entry: EntryModel) {
        data.isEditing.value.set(entry, true);
    };

    const isValid = function (validators: { [key: string]: TimeEntryValidator }): boolean {
        return Object.keys(validators).every(key => {
            const errors = validators[key].errors;
            return Object.keys(errors).every(errorKey => errors[errorKey].length === 0);
        });
    };

    const validateByKey = function (key: string, type: HoursOfOperationDisplayType, i: number) {
        data.timeValidators.value[`${key}-${type}-${i}`]?.validate(`startTime-${key}-${type}-${i}`);
        data.timeValidators.value[`${key}-${type}-${i}`]?.validate(`overlap-${key}-${HoursOfOperationDisplayType.AfterHours}-${i}`);
        data.timeValidators.value[`${key}-${type}-${i}`]?.validate(`overlap-${key}-${HoursOfOperationDisplayType.Open}-${i}`);
        data.timeValidators.value[`${key}-${type}-${i}`]?.validate(`range-${key}-${type}-${i}`);
        data.timeValidators.value[`${key}-${type}-${i}`]?.validate(`endTime-${key}-${type}-${i}`);
    }

    function removeEntriesButKeepCarryovers(key: string) {
        const day = data.allEntries.value[key];
        if (!day) return;

        const entries = day.entries ?? [];
        day.entries = entries.filter(isCarryoverFromPreviousDay);
    }

    return {
        isEntryModified(entry: EntryModel): boolean {
            return data.isEditing.value.get(entry) ?? false;
        },
       
        updateEntry: function (entry: EntryModel, key: string, type: HoursOfOperationDisplayType, i: number) {
            adjustPreviousAndNextDayOverlap(data.allEntries.value, key, entry);
            const entries = data.allEntries.value[key].entries;
            const actualEntry = entries.find(e => e === entry);

            if (!actualEntry) {
                return;
            }
            validateByKey(key, HoursOfOperationDisplayType.Open, i);
            validateByKey(key, HoursOfOperationDisplayType.AfterHours, i);
            ctx.emit("update-entries", data.allEntries.value, isValid(data.timeValidators.value));
            data.isEditing.value.set(actualEntry, false);

        },
        editStartTime: function (entry: EntryModel, key: string, type: HoursOfOperationDisplayType, i: number) {
            markEntryEdited(entry);
            validateByKey(key, type, i);
        },
        editEndTime: function (entry: EntryModel, key: string, type: HoursOfOperationDisplayType, i: number) {
            markEntryEdited(entry);
            validateByKey(key, type, i);
        },
        hasEndTimeError: function (key: string, type: HoursOfOperationDisplayType, i: number) {
            return data.timeValidators.value[`${key}-${type}-${i}`]?.errors[`overlap-${key}-${type}-${i}`] ||
                data.timeValidators.value[`${key}-${type}-${i}`]?.errors[`range-${key}-${type}-${i}`] ||
                data.timeValidators.value[`${key}-${type}-${i}`]?.errors[`endTime-${key}-${type}-${i}`]
        },
        hasStartTimeError: function (key: string, type: HoursOfOperationDisplayType, i: number) {
            return data.timeValidators.value[`${key}-${type}-${i}`]?.errors[`overlap-${key}-${type}-${i}`] ||
                data.timeValidators.value[`${key}-${type}-${i}`]?.errors[`range-${key}-${type}-${i}`] ||
                data.timeValidators.value[`${key}-${type}-${i}`]?.errors[`startTime-${key}-${type}-${i}`]
        },
        getEntries: function (dayData: HoursEntryModel, type: HoursOfOperationDisplayType) {
            if (!dayData || !dayData.entries) return [];
            return dayData.entries.filter((e: EntryModel) => e.type === type);
        },
        hasAfterHoursOnly(dayData: HoursEntryModel | undefined, afterHours24: boolean) {
            if (!dayData) return false;

            const entries = dayData.entries ?? [];
            let hasOpen = false;
            let hasAfter = false;

            for (const e of entries) {
                if (e.type === HoursOfOperationDisplayType.Open) hasOpen = true;
                else if (e.type === HoursOfOperationDisplayType.AfterHours) hasAfter = true;
                if (hasOpen && hasAfter) break;
            }

            const afterHoursActive = hasAfter || afterHours24;

            return (!hasOpen || (hasOpen && !dayData.enabled)) && afterHoursActive;
        },

        removeTimeEntry(key: string, entry: EntryModel) {
            const originalEntries = data.allEntries.value[key].entries;
            const indexInOriginal = originalEntries.indexOf(entry);
            if (indexInOriginal !== -1) {
                originalEntries.splice(indexInOriginal, 1);
            }

            data.isEditing.value?.delete(entry);
            updateEntryState(key);
            rebuildTimeValidators(data);
            ctx.emit("update-entries", data.allEntries.value, isValid(data.timeValidators.value));
            if ((!originalEntries || !originalEntries.length) && !data.allEntries.value[key].afterHours24) {
                data.allEntries.value[key].enabled = false;
            } else {
                data.allEntries.value[key].enabled = true;
            }
        },

        addTimeEntry: function (key: string) {
            data.entryToAdd.value[key] = undefined;
            data.allEntries.value[key].enabled = true;

            let lastItem = data.allEntries.value[key].entries.at(-1) || undefined;
            let typeToAdd = HoursOfOperationDisplayType.Open;

            if (data.allEntries.value[key].entries.length > 0) {
                typeToAdd = data.allEntries.value[key].entries[0].type === HoursOfOperationDisplayType.Open
                    ? HoursOfOperationDisplayType.AfterHours
                    : HoursOfOperationDisplayType.Open;
            } else {
                // Get previous day key
                const daysKeys = Object.keys(DAYS) as DayKey[];
                const prevDay = daysKeys[(DAYS[key as DayKey] + 6) % 7]; 

                const prevDayEntries = data.allEntries.value[prevDay]?.entries || [];

                // If previous day has exactly one entry of the same type
                if (prevDayEntries.length === 1 && prevDayEntries[0].type === typeToAdd) {
                    data.entryToAdd.value[key] = {
                        startTime: prevDayEntries[0].startTime,
                        endTime: prevDayEntries[0].endTime,
                        type: typeToAdd
                    };
                    return; 
                }
            }

            data.entryToAdd.value[key] = {
                startTime: lastItem?.endTime || "",
                endTime: "",
                type: typeToAdd
            };
        },

        toggleEnable: function (key: string) {
            if (data.allEntries.value[key].enabled) {
                data.allEntries.value[key].enabled = false;
                removeEntriesButKeepCarryovers(key);
                ctx.emit("update-entries", data.allEntries.value, isValid(data.timeValidators.value));
            } else {
                const daysKeys = Object.keys(DAYS) as DayKey[];
                const prevKey = daysKeys[(DAYS[key as DayKey] + 6) % 7]; 
                const prevDay = data.allEntries.value[prevKey];
                let copiedEntryModel = {} as HoursEntryModel;
                if (prevDay.entries.length > 0 && data.allEntries.value[key].entries.length == 0) {
                    copiedEntryModel.entries = [...prevDay.entries];
                    copiedEntryModel.afterHours24 = prevDay.afterHours24;
                    copiedEntryModel.enabled = true;

                    data.allEntries.value[key] = deepClone(copiedEntryModel);
                    rebuildTimeValidators(data);
                    ctx.emit("update-entries", data.allEntries.value, isValid(data.timeValidators.value));
                } else if (data.allEntries.value[key].entries.length > 0) {
                    data.allEntries.value[key].enabled = true;
                    ctx.emit("update-entries", data.allEntries.value, isValid(data.timeValidators.value));
                } else if (!prevDay.entries || prevDay.entries.length == 0 ) {
                    copiedEntryModel.afterHours24 = false;
                    copiedEntryModel.enabled = true;
                    let e = {} as EntryModel;
                    e.type =HoursOfOperationDisplayType.Open
                    e.startTime = "00:00";
                    e.endTime = "00:00";
                    copiedEntryModel.entries = [e];
                    data.allEntries.value[key] = deepClone(copiedEntryModel);
                    rebuildTimeValidators(data);
                    ctx.emit("update-entries", data.allEntries.value, isValid(data.timeValidators.value));
                }
            }
        },
        previousDayHasEntry: function (key: string) {
            // Get previous day key
            const daysKeys = Object.keys(DAYS) as DayKey[];
            const prevDay = daysKeys[(DAYS[key as DayKey] + 6) % 7]; 
            const prevDayEntries = data.allEntries.value[prevDay]?.entries || [];
            return prevDayEntries.length > 0
        },
      
        confirmEntryAdd: async function (key: string) {
            const newEntry = data.entryToAdd.value[key];

            if (newEntry) {
                removePlaceholderEntries(key);
                adjustPreviousAndNextDayOverlap(data.allEntries.value, key, newEntry);
                data.allEntries.value[key].entries.push(newEntry);
               
                if (newEntry.type == HoursOfOperationDisplayType.AfterHours) {
                    data.allEntries.value[key].afterHours24 = false;
                }

                data.allEntries.value[key].enabled = true;
                rebuildTimeValidators(data);
                ctx.emit("update-entries", data.allEntries.value, isValid(data.timeValidators.value));
                data.entryToAdd.value[key] = undefined;
            }
           
        },
        discardEntryAdd: function (key: string) {
            data.entryToAdd.value[key] = undefined;
            updateEntryState(key);
        },
        select24: function (key: string) {
            if (!data.allEntries.value[key].afterHours24 && data.allEntries.value[key].entries.length == 0) {
                data.allEntries.value[key].enabled = false;
            }
            if (data.allEntries.value[key].afterHours24) {
                data.allEntries.value[key].enabled = true;
            }
            rebuildTimeValidators(data);
            ctx.emit("update-entries", data.allEntries.value, isValid(data.timeValidators.value));
        }

    };
}

export default defineComponent({
    props: {
        entries: {
            type: Object as () => { [key: string]: HoursEntryModel },
            required: false,
            default: {}
        },
        hoursType: String
    },
    components: {
        'error': validationError
    },
    emits: ['update-entries'],
    setup: function (props, ctx) {
        let entries = props.entries;
        var data = getInitialData(entries);
        var methods = getMethods(data, ctx);

        onMounted(() => {
            rebuildTimeValidators(data);
        });

        const isAnyEditing = computed(() => data.isEditing.value.size > 0);

        watch(
            () => [
                props.entries,
            ],
            ([newEdit, newOriginal]) => {
                const newVal = newEdit ?? newOriginal ?? undefined;
                if (newVal != undefined) {
                    if (isAnyEditing.value) return; // don�t overwrite mid-edit
                    data.allEntries.value = getCleanEntries(newVal);
                    rebuildTimeValidators(data);
                }
            },
            {deep: true}
        );

        return {
            ...data,
            ...methods,
            'HoursOfOperationDisplayType': HoursOfOperationDisplayType,
            'requireMidnightSplit': requiresMidnightSplit
        }
    }
});

